import * as ApplicationTypes from "../../middleware/Constants/ApplicationTypes";
import * as ContactUtils from "./ContactUtils";
import * as ApplicationConstant from "../Constants/ApplicationConstant";
import * as PathUtils from "./PathUtils";

export { debounce, clearDebounce } from "./DebounceUtils";
export { isInvoiceAccountFullyFilled } from "./BillingUtils"

export const applicationsFilters = (intl) => {
    return {
        My: {
            value: "my",
            label: intl?.messages.applicationFilterMyApplications,
        },
        All: {
            value: "all",
            label: intl?.messages.applicationFilterAll,
        },
        InDeployment: {
            value: ApplicationTypes.Env.InDeployment,
            label: intl?.messages.applicationFilterInDeployment
        },
        Deployed: {
            value: ApplicationTypes.Env.Deployed,
            label: intl?.messages.applicationFilterDeployed
        },
        NotDeployed: {
            value: ApplicationTypes.Env.NotDeployed,
            label: intl?.messages.applicationFilterNotDeployed
        },
        Blocked: {
            value: ApplicationTypes.State.Blocked,
            label: intl?.messages.applicationFilterBlocked
        }
    };
};

export const getApplicationStateAndStyle = (application) => {
    let label = "NOT DEPLOYED";
    let className = "notDeployed"
    if (!application) {
        return { label, className };
    }
    switch (application.env) {
        case ApplicationTypes.Env.Deployed:
            switch (application.state) {
                case ApplicationTypes.State.Active:
                    label = "RUNNING";
                    className = label.toLocaleLowerCase();
                    break;
                case ApplicationTypes.State.Stopped:
                    label = "STOPPED";
                    className = label.toLocaleLowerCase();
                    break;
                case ApplicationTypes.State.Blocked:
                    label = "BLOCKED";
                    className = label.toLocaleLowerCase();
                    break;
                case ApplicationTypes.State.Deleted:
                    label = "DELETED";
                    className = label.toLocaleLowerCase();
                    break;
                default:
                    label = "RUNNING";//"NEW"
                    className = label.toLocaleLowerCase();
                    break;
            }
            break;
        case ApplicationTypes.Env.InDeployment:
            label = "IN DEPLOYMENT";
            className = "pending";
            break;
        default:
            label = "NOT DEPLOYED";
            className = "notDeployed";
            break;
    }
    return { label, className };
};

export const getDeveloperContact = (id, contactListObject, defaultContact) => {
    let foundDeveloper = defaultContact;
    let isNoLongerDeveloper = false;

    let foundIndex = contactListObject?.developers?.findIndex(developer => developer.id === id);
    if (foundIndex >= 0) {
        foundDeveloper = contactListObject.developers[foundIndex];
    } else {
        isNoLongerDeveloper = true;
        foundIndex = contactListObject?.contacts?.findIndex(contact => contact.id === id);
        if (foundIndex >= 0) {
            foundDeveloper = contactListObject.contacts[foundIndex];
        }
    }

    return { foundDeveloper, isNoLongerDeveloper };
};

export const getDeveloperDisplayName = (id, contactListObject) => {
    const { foundDeveloper, isNoLongerDeveloper } = getDeveloperContact(id, contactListObject);

    let displayName = ContactUtils.getDisplayName(foundDeveloper);
    if (displayName.length === 0) {
        displayName = id;
    }
    return { displayName, isNoLongerDeveloper };
};

export const getCompanyName = (id, contactListObject) => {
    let foundDeveloper = null;

    let foundIndex = contactListObject?.developers.findIndex(developer => developer.id === id);
    if (foundIndex >= 0) {
        foundDeveloper = contactListObject.developers[foundIndex];
    } else {
        foundIndex = contactListObject?.contacts.findIndex(contact => contact.id === id);
        if (foundIndex >= 0) {
            foundDeveloper = contactListObject.contacts[foundIndex];
        }
    }
    let companyName = ContactUtils.getCompanyName(foundDeveloper);
    return companyName;
}

export const getApplicationById = (appId, applicationList) => {
    let foundApplication = applicationList?.filter(application => application.id === appId);
    return foundApplication?.length > 0 ? foundApplication[0] : null;
};

export const getApplicationByName = (appName, applicationList) => {
    let foundApplication = applicationList?.filter(application => application.name === appName);
    return foundApplication?.length > 0 ? foundApplication[0] : null;
};

export const isApplicationCreatePath = (location) => {
    if (location && location.pathname.startsWith(ApplicationConstant.APPLICATION_PATH + ApplicationConstant.CREATE_PATH)) {
        return true;
    }
    return false;
}

export const isApplicationEditPath = (location) => {
    if (location && location.pathname.startsWith(ApplicationConstant.APPLICATION_PATH + ApplicationConstant.EDIT_PATH)) {
        return true;
    }
    return false;
}

export const isApplicationStatisticsPath = (location) => {
    if (location && location.pathname.startsWith(ApplicationConstant.APPLICATION_PATH + ApplicationConstant.STATISTICS_PATH)) {
        return true;
    }
    return false;
}

export const isApplicationPath = (location) => {
    if (location && location.pathname.startsWith(ApplicationConstant.APPLICATION_PATH)) {
        return true;
    }
    return false;
}

export const isApplicationsPath = (location) => {
    if (location && location.pathname.startsWith(ApplicationConstant.APPLICATIONS_PATH)) {
        return true;
    }
    return false;
}

export const getApplicationIdFromLocation = (location) => {
    return PathUtils.getQueryParameterInPathString(location.search, ApplicationConstant.ApplicationTagName);
}

export const getApplicationSearchValue = (location) => {
    const value = PathUtils.getQueryParameterInPathString(location.search, ApplicationConstant.ApplicationSearchTagName);
    return value ?? "";
}

export const getApplicationFilterValue = (location, defaultValue) => {
    let value = PathUtils.getQueryParameterInPathString(location.search, ApplicationConstant.ApplicationFilterTagName);
    return value ?? defaultValue;
}

export const setApplicationSearchValueUrl = (location, value) => {
    return PathUtils.addOrUpdateQueryParameterToPathString(location.search, ApplicationConstant.ApplicationSearchTagName, value);
}

export const setApplicationFilterValueUrl = (location, value) => {
    return PathUtils.addOrUpdateQueryParameterToPathString(location.search, ApplicationConstant.ApplicationFilterTagName, value);
}

export const isMyApplication = (application, user) => {
    if (!application || !user) {
        return false;
    }
    return application.ownerId === user.id;
};

export const myApplications = (applicationList, user) => {
    return applicationList?.filter(application => isMyApplication(application, user));
}

export const myDeployedApplications = (applicationList, user) => {
    return applicationList?.filter(application => isMyApplication(application, user) && application.env === ApplicationTypes.Env.Deployed);
};

export const getPushNotification = (application) => {
    if (!application || !application.pushNotificationSettings) {
        return [];
    }

    return application.pushNotificationSettings;
}

export const isMobileApplication = (application) => {
    if (!application) {
        return false;
    }
    return application.type === ApplicationConstant.Type.Mobile;
}

export const isBusinessApplication = (application) => {
    if (!application) {
        return false;
    }
    return application.kpi === ApplicationConstant.ModelType.Business;
}

export const isInternalApplication = (application) => {
    if (!application) {
        return false;
    }
    return application.kpi === ApplicationConstant.ModelType.Internal;
}

export const getApplicationListNames = (applicationIdList, applications) => {
    let applicationsName = [];
    if (applicationIdList && applicationIdList.length > 0) {
        applicationsName = applicationIdList.map(applicationId => getApplicationById(applicationId, applications)?.name);
    }
    const message = applicationsName.toString().replace(/,/g, "\n");
    return message;
}

export const areOnlyBusinessApplications = (applicationIdList, applications) => {
    let onlyBusinessApplication = false;
    if (applicationIdList && applicationIdList.length > 0) {
        onlyBusinessApplication = true;
        applicationIdList.forEach(applicationId => {
            if (!isBusinessApplication(getApplicationById(applicationId, applications)))
                onlyBusinessApplication = false;
        });
    }
    return onlyBusinessApplication;
}

export const areOnlyInternalApplications = (applicationIdList, applications) => {
    let onlyBusinessApplication = false;
    if (applicationIdList && applicationIdList.length > 0) {
        onlyBusinessApplication = true;
        applicationIdList.forEach(applicationId => {
            if (!isInternalApplication(getApplicationById(applicationId, applications)))
                onlyBusinessApplication = false;
        });
    }
    return onlyBusinessApplication;
}

export const areOnlyMyApplications = (contactId, applicationList, selectedApplicationIdList = null) => {
    let onlyMyApplications = false;
    if (applicationList && applicationList.length > 0) {
        if (selectedApplicationIdList && selectedApplicationIdList.length > 0) {
            onlyMyApplications = !applicationList.some(application => selectedApplicationIdList.indexOf(application.id) >= 0 && application.ownerId !== contactId);
        } else {
            onlyMyApplications = !applicationList.some(application => application.ownerId !== contactId);
        }
    }
    return onlyMyApplications;
};

export const isSandBoxApplication = (location) => {
    return PathUtils.getQueryParameterInPathString(location.search, ApplicationConstant.ApplicationSandBoxTagName) === "true";
}

export const isSandBoxPath = (location) => {
    if (location && location.pathname.startsWith(ApplicationConstant.SANDBOX_PATH)) {
        return true;
    }
    return false;
}

export const filterApplications = (filterValue, applicationList, user) => {
    let filteredApplications = applicationList;

    if (applicationList && applicationList.length > 0) {
        if (filterValue === applicationsFilters().All.value) {
            filteredApplications = applicationList;
        } else if (filterValue === applicationsFilters().My.value) {
            filteredApplications = myApplications(applicationList, user);
        } else if (filterValue === applicationsFilters().Blocked.value) {
            // return props.applications.filter((app) => app.state === filterValue);
            filteredApplications = applicationList?.filter(app => app.state === filterValue);
        } else if (filterValue === applicationsFilters().InDeployment.value ||
            filterValue === applicationsFilters().Deployed.value ||
            filterValue === applicationsFilters().NotDeployed.value
        ) {
            // return props.applications.filter((app) => app.env === filterValue);
            filteredApplications = applicationList?.filter(app => app.env === filterValue);
        }
    }
    return filteredApplications;
}

export const searchApplicationByName = (value, applicationList) => {
    let foundApplications = applicationList;
    if (value)
        foundApplications = foundApplications?.filter(application => application.name?.toLowerCase().match(value.toLowerCase()));
    return foundApplications;
};

export function applicationSortCompareFunction(appA, appB, sortOptions) {
    let item1 = appA[sortOptions?.dataFieldName] || "unknown";
    let item2 = appB[sortOptions?.dataFieldName] || "unknown";

    if (sortOptions?.dataFieldName === ApplicationConstant.dataFieldName.ownerName) {//ownername
        const foundContactA = ContactUtils.getContactById(appA.ownerId, sortOptions.contacts);
        item1 = ContactUtils.getDisplayName(foundContactA) || appA.id;
        const foundContactB = ContactUtils.getContactById(appB.ownerId, sortOptions.contacts);
        item2 = ContactUtils.getDisplayName(foundContactB) || appB.id;
    }

    if (sortOptions?.dataFieldName === ApplicationConstant.dataFieldName.ownerCompany) {//ownerCompany
        const foundContactA = ContactUtils.getContactById(appA.ownerId, sortOptions.contacts);
        item1 = ContactUtils.getCompanyName(foundContactA);
        const foundContactB = ContactUtils.getContactById(appB.ownerId, sortOptions.contacts);
        item2 = ContactUtils.getCompanyName(foundContactB);
    }

    if (sortOptions?.dataFieldName === ApplicationConstant.dataFieldName.envOrState) {//env or state
        item1 = getApplicationStateAndStyle(appA).label;
        item2 = getApplicationStateAndStyle(appB).label;
    }

    if (item1.toLowerCase() < item2.toLowerCase())
        return sortOptions?.order === "asc" ? -1 : 1;

    if (item1.toLowerCase() > item2.toLowerCase())
        return sortOptions?.order === "asc" ? 1 : -1;

    return 0;
};